import { useState } from 'react';
import moment from 'moment';
import { useMutation } from 'react-apollo';

import getConfig from '../../../../util/config';
import { externalShippingAddressesCreate as externalShippingAddressesCreateMutation } from '../../../../graphql/mutations/externalShippingAddress';
import getUserForAccountCardConfigurationQuery from '../../graphql/queries/getUserForAccountCardConfiguration';
import { getErrorString } from '../../../../util/normalize-errors';
import { sentryCaptureException } from '../../../../util/sentry';
import { convertStateToAbbreviation } from '../../../../apps/pro/views/AccountAddressBook/components/AddressBookCSVUpload/components/ColumnMatcher/helpers';

/**
 * Contact spreadsheet import hook.
 *
 * @param {Object} [options] The options.
 * @param {function} [options.onSuccess] Success callback.
 * @param {function} [options.onImport] Import callback, fires every time there is a successful import.
 * @returns {{setInvalidRows: React.Dispatch<React.SetStateAction<*[]>>, importData: unknown, setImportData: React.Dispatch<React.SetStateAction<unknown>>, loading: boolean, error: string, invalidRows: *[], onDataCreate: (function(*=, *=): Promise<undefined>)}}
 */
const useContactSpreadsheetImport = ({ onSuccess, onImport } = {}) => {
  const config = getConfig();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importData, setImportData] = useState(null);
  const [invalidRows, setInvalidRows] = useState([]);
  const [externalShippingAddressesCreate] = useMutation(
    externalShippingAddressesCreateMutation,
  );

  const getInputFromColumnData = rows => {
    return rows.map(row => {
      return {
        name: `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim(),
        address_line_1: row.addressLine1,
        address_line_2: row.addressLine2 ?? '',
        city: row.city,
        state: convertStateToAbbreviation(row.state),
        post_code: row.zipCode,
        country_id: config.country.usa.id,
        phone_number: (row.phoneNumber ?? '').toString(),
        email_address: row.emailAddress ?? '',
        date_of_birth: moment(row.dateOfBirth).isValid()
          ? moment(row.dateOfBirth).format('YYYY-MM-DD')
          : // Make sure we pass dateOfBirth here so that it can be validated & errored out.
            row.dateOfBirth || null,
      };
    });
  };

  const onDataCreate = async (data, convertFromRaw = true) => {
    if (loading) {
      return;
    }

    const rows = convertFromRaw ? getInputFromColumnData(data) : data;

    setLoading(true);
    setError('');

    try {
      const response = await externalShippingAddressesCreate({
        variables: {
          input: {
            external_shipping_addresses: rows,
            skip_errors: true,
          },
        },
        refetchQueries: [
          {
            query: getUserForAccountCardConfigurationQuery,
          },
        ],
      });

      await onImport?.(
        response.data.externalShippingAddressesCreate
          .external_shipping_addresses,
      );

      const invalidAddresses =
        response.data.externalShippingAddressesCreate.invalid_addresses;
      if (invalidAddresses?.length > 0) {
        setInvalidRows(invalidAddresses.map(JSON.parse));
      } else {
        onSuccess?.(
          response.data.externalShippingAddressesCreate
            .external_shipping_addresses,
        );
      }
    } catch (responseError) {
      setError(getErrorString(responseError));
      sentryCaptureException(responseError);
    } finally {
      setLoading(false);
    }
  };

  return {
    importData,
    setImportData,
    setInvalidRows,
    invalidRows,
    loading,
    error,
    onDataCreate,
  };
};

export default useContactSpreadsheetImport;
